import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Typography } from "@mui/material";
import { ChatContainer, MessagesArea } from "@components/Chat/Chat.styles";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { useCargoMessages } from "../hooks/useCargoMessages";
import { useCargoOperationDetail } from "../hooks/useCargoOperationDetail";
import { useIsCargoOpsShipper } from "../hooks/useIsCargoOpsShipper";
import { useIsCargoOpsShippingAgent } from "../hooks/useIsCargoOpsShippingAgent";
import { useMessageDraftStore } from "../hooks/useMessageDraftStore";
import { usePollingGate } from "../hooks/usePollingGate";
import { useSendCargoMessage } from "../hooks/useSendCargoMessage";
import { groupFilesByMessage, sortMessages, type MessageRole } from "../utils/messageThread";
import CargoMessageBubble from "./CargoMessageBubble";
import MessageComposer from "./MessageComposer";
import ThreadPicker from "./ThreadPicker";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.detail.messages`;
const NEAR_BOTTOM_PX = 80;
const NO_CODES: string[] = [];

const useMessageRole = (): MessageRole | null => {
  const isOperations = useAuthStore((state) => state.isOperationsUser);
  const isShippingAgent = useIsCargoOpsShippingAgent();
  const isShipper = useIsCargoOpsShipper();
  if (isOperations) return "OPERATIONS";
  if (isShippingAgent) return "SHIPPING_AGENT";
  return isShipper ? "SHIPPER" : null;
};

const MessagesTab: React.FC<{ operationId: string }> = ({ operationId }) => {
  const { t } = useTranslation();
  const role = useMessageRole();
  const user = useAuthStore((state) => state.user);
  const { busy } = usePollingGate();
  const detail = useCargoOperationDetail(operationId, { paused: busy });
  const storedThread = useMessageDraftStore((state) => state.selectedThread[operationId]);
  const selectThread = useMessageDraftStore((state) => state.selectThread);
  const isShipper = role === "SHIPPER";

  const shipperCodes = useMemo(() => {
    const codes = (detail.data?.shippers ?? []).map((row) => row.shipperCompanyCode);
    return Array.from(new Set(codes.filter((code): code is string => Boolean(code))));
  }, [detail.data?.shippers]);

  let threadKey: string | undefined;
  if (isShipper) {
    threadKey = user?.companyId || undefined;
  } else if (shipperCodes.length > 0) {
    threadKey =
      storedThread && shipperCodes.includes(storedThread) ? storedThread : shipperCodes[0];
  }

  const sender = useSendCargoMessage({
    operationId,
    threadKey,
    role,
    vesselCallId: detail.data?.vesselCallId,
  });
  const chat = useCargoMessages(operationId, threadKey, { sending: sender.busy });

  const messages = useMemo(() => sortMessages(chat.messages ?? []), [chat.messages]);
  const filesByMessage = useMemo(() => groupFilesByMessage(chat.files ?? []), [chat.files]);

  const areaRef = useRef<HTMLDivElement | null>(null);
  const atBottom = useRef(true);
  const seen = useRef<{ threadKey?: string; ids: Set<string> } | null>(null);
  const [announcement, setAnnouncement] = useState("");

  useEffect(() => {
    if (!chat.messages) return;
    const ids = new Set(chat.messages.map((message) => message.id));
    if (seen.current?.threadKey === threadKey) {
      const arrivals = chat.messages.filter(
        (message) => !seen.current?.ids.has(message.id) && message.authorId !== user?.email,
      );
      if (arrivals.length > 0) {
        setAnnouncement(t(`${L}.new-messages`, { count: arrivals.length }));
      }
    } else {
      setAnnouncement("");
    }
    seen.current = { threadKey, ids };
  }, [chat.messages, threadKey, user?.email, t]);

  const lastMessage = messages[messages.length - 1];
  useLayoutEffect(() => {
    const area = areaRef.current;
    if (!area) return;
    const own = lastMessage?.authorId === user?.email;
    if (atBottom.current || own) {
      area.scrollTop = area.scrollHeight;
    }
  }, [lastMessage?.id, lastMessage?.authorId, user?.email, threadKey]);

  useLayoutEffect(() => {
    atBottom.current = true;
  }, [threadKey]);

  if (role === null) return null;

  if (isShipper && !threadKey) {
    return (
      <Typography data-testid="drybulk-messages-unavailable" color="text.secondary">
        {t(`${L}.thread-unavailable`)}
      </Typography>
    );
  }

  if (!isShipper && detail.isLoading) {
    return (
      <div className="relative min-h-24" data-testid="drybulk-messages-tab">
        <LoadingOverlay loading testId="drybulk-messages-loading" />
      </div>
    );
  }

  if (!isShipper && shipperCodes.length === 0) {
    return (
      <Typography data-testid="drybulk-messages-no-shipper" color="text.secondary">
        {detail.isError ? t(`${L}.unavailable`) : t(`${L}.no-shipper`)}
      </Typography>
    );
  }

  const onScroll = () => {
    const area = areaRef.current;
    if (area) {
      atBottom.current = area.scrollHeight - area.scrollTop - area.clientHeight <= NEAR_BOTTOM_PX;
    }
  };

  const changeThread = (next: string) => {
    if (next === threadKey) return;
    chat.trackThreadSwitch();
    selectThread(operationId, next);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="drybulk-messages-tab">
      <ThreadPicker
        isShipper={isShipper}
        shipperCodes={shipperCodes.length ? shipperCodes : NO_CODES}
        value={threadKey}
        onChange={changeThread}
      />
      <ChatContainer sx={{ width: "100%" }}>
        <MessagesArea
          ref={areaRef}
          onScroll={onScroll}
          sx={{ position: "relative" }}
          data-testid="drybulk-messages-list"
        >
          <LoadingOverlay loading={chat.isLoading} testId="drybulk-messages-loading" />
          {chat.isError && (
            <Typography data-testid="drybulk-messages-error" color="text.secondary">
              {t(`${L}.unavailable`)}
            </Typography>
          )}
          {chat.messages && messages.length === 0 && (
            <Typography data-testid="drybulk-messages-empty" color="text.secondary">
              {t(`${L}.empty`)}
            </Typography>
          )}
          {messages.map((message) => (
            <CargoMessageBubble
              key={message.id}
              message={message}
              files={filesByMessage.get(message.id) ?? []}
              isOwn={message.authorId === user?.email}
              pending={sender.pendingAttach?.[message.id]}
              retrying={sender.retryingId === message.id}
              onRetry={() => void sender.retry(message.id)}
              onRepick={(files) => void sender.repick(message.id, files)}
            />
          ))}
        </MessagesArea>
        {chat.isFilesError && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="drybulk-messages-files-unavailable"
          >
            {t(`${L}.files-unavailable`)}
          </Typography>
        )}
        <MessageComposer controller={sender} />
      </ChatContainer>
      <div role="status" aria-live="polite" className="sr-only" data-testid="drybulk-messages-live">
        {announcement}
      </div>
    </div>
  );
};

export default MessagesTab;
